import type { CodeBlockNode, HeadingNode, LinkNode, ListNode, Node } from "./commonmark/node";

export interface BaseASTNode {
  id: number;
  type: string;
  literal: string | null;
  children: ASTNode[];
}

export interface LinkASTNode extends BaseASTNode {
  type: "link" | "image";
  destination: string | null;
}

export interface HeadingASTNode extends BaseASTNode {
  type: "heading";
  level: number;
}

export interface ListASTNode extends BaseASTNode {
  type: "list" | "item";
  listData: {
    type: "bullet" | "ordered";
    start: number;
    tight: boolean;
    delimiter: string;
  } | null;
}
export interface ItemASTNode extends BaseASTNode {
  type: "item";
  listData: {
    // The parser copies its list's data onto every item, so an item knows which kind of list it is in.
    type: "bullet" | "ordered";
    task: boolean;
    checked: boolean;
  };
}

export interface CodeBlockASTNode extends BaseASTNode {
  type: "codeBlock";
  info: string | null;
}

export interface TableWrapperASTNode extends BaseASTNode {
  type: "tableWrapper";
}

export interface DocASTNode extends BaseASTNode {
  type: "document";
}

export type ASTNode =
  | BaseASTNode
  | LinkASTNode
  | HeadingASTNode
  | ListASTNode
  | ItemASTNode
  | CodeBlockASTNode
  | TableWrapperASTNode
  | DocASTNode;

/**
 * Concatenates the text a node holds below it. The parser sets `literal` only on leaf nodes, so a
 * container's text — an image's alt text, which it keeps as children the way it keeps a link's
 * label — is only reachable from the descendants. A line break carries neither, so it stands in for
 * itself, matching the newline the report export writes for the same node.
 */
export function astNodeText(node: ASTNode): string {
  if (node.type === "softbreak" || node.type === "linebreak") {
    return "\n";
  }
  if (node.children.length === 0) {
    return node.literal ?? "";
  }
  return node.children.map(astNodeText).join("");
}

/**
 * Converts parser nodes into AST nodes, handing back the matching node of `previous` (by position)
 * wherever the result would be identical to it, so a caller diffing against `previous` can stop at
 * the first node it already holds.
 */
export function createAST(nodes: Node[], previous: readonly ASTNode[] = []): ASTNode[] {
  return nodes.map((node, i) => toAST(node, previous[i]));
}

const SCRIPT_SCHEME = /^\s*(javascript|vbscript|data):/i;

// Every renderer, and every consumer override of the link component, reads the destination from
// here, so a link that would run script on click is cut once for all of them.
const safeLinkDestination = (destination: string | null): string | null =>
  destination && SCRIPT_SCHEME.test(destination) ? null : destination;

function toAST(node: Node, previous: ASTNode | undefined): ASTNode {
  const { id, literal } = node;
  // A table sits one level down, inside its wrapper.
  const previousChildren = (node.type === "table" ? previous?.children[0] : previous)?.children;
  const children: ASTNode[] = [];
  for (let child = node.firstChild; child; child = child.next) {
    children.push(toAST(child, previousChildren?.[children.length]));
  }

  let ast: ASTNode;
  switch (node.type) {
    case "link":
    case "image":
      ast = {
        id,
        type: node.type,
        literal,
        children,
        destination:
          node.type === "link" ? safeLinkDestination((node as LinkNode).destination) : (node as LinkNode).destination,
      } satisfies LinkASTNode;
      break;
    case "heading":
      ast = { id, type: "heading", literal, children, level: (node as HeadingNode).level } satisfies HeadingASTNode;
      break;
    case "list": {
      const { type, start, tight, delimiter } = (node as ListNode).listData!;
      ast = { id, type: "list", literal, children, listData: { type, start, tight, delimiter } } satisfies ListASTNode;
      break;
    }
    case "item": {
      const { type, task, checked } = (node as ListNode).listData!;
      ast = { id, type: "item", literal, children, listData: { type, task, checked } } satisfies ItemASTNode;
      break;
    }
    case "codeBlock":
      ast = { id, type: "codeBlock", literal, children, info: (node as CodeBlockNode).info } satisfies CodeBlockASTNode;
      break;
    case "table":
      ast = { id, type: "tableWrapper", literal: null, children: [{ id, type: "table", literal, children }] };
      break;
    default:
      ast = { id, type: node.type, literal, children };
  }
  return previous && sameAST(previous, ast) ? previous : ast;
}

function sameAST(a: ASTNode, b: ASTNode): boolean {
  if (a.type !== b.type || a.literal !== b.literal || a.children.length !== b.children.length) {
    return false;
  }
  if (b.type === "tableWrapper") {
    return sameAST(a.children[0]!, b.children[0]!);
  }
  if (!a.children.every((child, i) => child === b.children[i])) {
    return false;
  }
  // The type check above makes each pair below the same node type.
  switch (b.type) {
    case "link":
    case "image":
      return (a as LinkASTNode).destination === (b as LinkASTNode).destination;
    case "heading":
      return (a as HeadingASTNode).level === (b as HeadingASTNode).level;
    case "codeBlock":
      return (a as CodeBlockASTNode).info === (b as CodeBlockASTNode).info;
    case "list": {
      const [x, y] = [(a as ListASTNode).listData!, (b as ListASTNode).listData!];
      return x.type === y.type && x.start === y.start && x.tight === y.tight && x.delimiter === y.delimiter;
    }
    case "item": {
      const [x, y] = [(a as ItemASTNode).listData, (b as ItemASTNode).listData];
      return x.type === y.type && x.task === y.task && x.checked === y.checked;
    }
    default:
      return true;
  }
}
